import {isFeatureEnabled} from 'feature-flags';
import {useLocale} from 'platform/locale';

import {useCallback, useMemo} from 'react';

import {isNotNil} from 'ramda';

import {useGetCountriesQuery} from '@dms/api/core';
import {useGetAllApiEnumsQuery} from '@dms/api/sales';
import {GetAllApiEnumsApiResponse} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {FeatureSubCategory} from '@dms/shared';

import {ApiEnum} from '../types/ApiEnum';
import {buildFeatureEnumTree} from '../utils/buildFeatureEnumTree';
import {getBodyStyleEnum} from '../utils/getBodyStyleEnum';
import {getDefaultLabel} from '../utils/getDefaultLabel';
import {getEnumByProperty, PropertyName} from '../utils/getEnumByProperty';

/**
 * Feature subcategories are `feature_category` keys that have a parent category, in catalogue order.
 */
function getFeatureSubCategories(enums?: GetAllApiEnumsApiResponse): FeatureSubCategory[] {
  return getEnumByProperty('feature_category', enums)
    .filter((category) => isNotNil(category.parent))
    .map((category) => ({
      key: category.const_key,
      label: getDefaultLabel(category),
      parent: category.parent,
    }));
}

export function useApiEnums(options: {flat?: boolean} = {flat: false}) {
  const {language} = useLocale();
  const {
    data: enums,
    isLoading,
    isError,
  } = useGetAllApiEnumsQuery({
    lang: [language],
    // Vans are requested for the `van_style` body styles; `getEnumByProperty` reads every other
    // property from the passenger car set so van keys do not leak into the remaining dropdowns.
    // Kept on the flag rather than on the tab, so switching tabs does not split the RTK cache.
    vehicleType: isFeatureEnabled(featureFlags.SOURCING_VAN_FILTERS)
      ? ['VEHICLETYPE_PASSENGER_CAR', 'VEHICLETYPE_VAN']
      : ['VEHICLETYPE_PASSENGER_CAR'],
  });
  const {data: countries} = useGetCountriesQuery();

  const getEnum = useCallback(
    (propertyName: PropertyName): ApiEnum => {
      // Van body styles live under `van_style`; sourcing offers them in the same `bodyType` filter,
      // on classifieds and auctions alike.
      if (propertyName === 'car_style') {
        return getBodyStyleEnum(enums, isFeatureEnabled(featureFlags.SOURCING_VAN_FILTERS));
      }

      if (propertyName === 'body_color') {
        return getEnumByProperty(propertyName, enums).map((item) => ({
          key: item.const_key,
          value: item.const_key,
          label: getDefaultLabel(item),
          colorCode: item.properties?.[0]?.value,
        }));
      }

      if (propertyName === 'country') {
        return (
          countries?.map((country) => ({
            key: country.code,
            value: country.code,
            label: country.name,
          })) ?? []
        );
      }

      if (propertyName === 'feature' && !options.flat) {
        return buildFeatureEnumTree(enums);
      }

      return getEnumByProperty(propertyName, enums).map((item) => ({
        key: item.const_key,
        value: item.const_key,
        label: getDefaultLabel(item),
      }));
    },
    [countries, enums, options.flat]
  );

  const isSubCategoriesEnabled = isFeatureEnabled(featureFlags.SALES_FEATURE_SUBCATEGORIES);

  // useMemo for correctness: consumers pass this to memoized pickers, which need a stable reference.
  // Undefined while the flag is off, so pickers keep the flat, ungrouped feature list.
  const featureSubCategories = useMemo(
    () => (isSubCategoriesEnabled ? getFeatureSubCategories(enums) : undefined),
    [enums, isSubCategoriesEnabled]
  );

  return {enums, getEnum, featureSubCategories, isLoading, isError};
}
